import { useEffect, useState } from "react";
import { GitCompare, PawPrint } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getStyleById } from "@/lib/groomingStyles";
import { sortHistory } from "@/lib/dogProfiles";

function fmtDate(ts) {
  try {
    return new Date(ts).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

function detailsOf(h) {
  return [
    h.bodyLength && `Body ${h.bodyLength}`,
    h.faceStyle && `Face ${h.faceStyle}`,
    h.earStyle && `Ears ${h.earStyle}`,
    h.tailStyle && `Tail ${h.tailStyle}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

function LookColumn({ dog, entry, testid }) {
  const style = getStyleById(entry.styleId) || { name: entry.styleName };
  return (
    <div className="flex-1 min-w-0" data-testid={testid}>
      <div className="aspect-square w-full rounded-2xl overflow-hidden bg-[#f3efe6] border border-[#eae4d9]">
        {entry.previewImage ? (
          <img src={entry.previewImage} alt={style?.name} className="w-full h-full object-cover" />
        ) : dog.originalPhoto ? (
          <img src={dog.originalPhoto} alt="Dog" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#64748b]">
            <PawPrint className="w-8 h-8" />
          </div>
        )}
      </div>
      <div className="mt-2.5 text-center">
        <div className="text-sm font-bold text-[#0f172a] truncate">{entry.label || style?.name || "Custom"}</div>
        {entry.label && style?.name && (
          <div className="text-[11px] text-[#94a3b8] truncate">{style.name}</div>
        )}
        <div className="text-[11px] text-[#64748b] mt-0.5">{fmtDate(entry.createdAt)}</div>
        {detailsOf(entry) && (
          <div className="text-[11px] text-[#64748b] mt-1 leading-snug">{detailsOf(entry)}</div>
        )}
      </div>
    </div>
  );
}

export default function CompareModal({ dog, onClose }) {
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    setSelected([]);
  }, [dog?.id]);

  if (!dog) return null;

  const history = sortHistory(dog.history);

  const toggle = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  };

  const ready = selected.length === 2;
  const [a, b] = selected.map((id) => history.find((h) => h.id === id));

  return (
    <Dialog open={!!dog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg bg-[#fdfbf7] border-[#eae4d9] p-0 overflow-hidden"
        data-testid="compare-modal"
      >
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="flex items-center gap-2 text-[#0f172a] font-display text-xl font-extrabold tracking-tight">
            <GitCompare className="w-5 h-5 text-[#ff6b4a]" />
            Compare two cuts
          </DialogTitle>
          <p className="text-sm text-[#64748b]">
            {ready
              ? `${dog.name || "Your pup"} — side by side`
              : `Pick two saved looks for ${dog.name || "your pup"} to compare.`}
          </p>
          <DialogDescription className="sr-only">
            Compare two saved grooming looks for this dog side by side.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 pb-5 max-h-[60vh] overflow-y-auto">
          {ready ? (
            <>
              <div className="flex items-start gap-4">
                <LookColumn dog={dog} entry={a} testid="compare-look-a" />
                <div className="self-center shrink-0 w-8 h-8 rounded-full bg-white border border-[#eae4d9] flex items-center justify-center text-xs font-bold text-[#94a3b8]">
                  VS
                </div>
                <LookColumn dog={dog} entry={b} testid="compare-look-b" />
              </div>
              <button
                type="button"
                onClick={() => setSelected([])}
                data-testid="compare-change-selection"
                className="mt-5 w-full rounded-full bg-white hover:bg-[#f3efe6] text-[#0f172a] text-sm font-semibold px-4 py-2.5 border border-[#eae4d9] transition"
              >
                Change selection
              </button>
            </>
          ) : (
            <div className="grid grid-cols-3 gap-2.5" data-testid="compare-picker">
              {history.map((h) => {
                const style = getStyleById(h.styleId) || { name: h.styleName };
                const isSel = selected.includes(h.id);
                const order = selected.indexOf(h.id) + 1;
                return (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => toggle(h.id)}
                    data-testid={`compare-pick-${h.id}`}
                    aria-pressed={isSel}
                    className={`relative rounded-2xl overflow-hidden border text-left transition ${
                      isSel ? "border-[#ff6b4a] ring-2 ring-[#ff6b4a]/40" : "border-[#eae4d9] hover:border-[#d6cdba]"
                    }`}
                  >
                    <div className="aspect-square w-full bg-[#f3efe6]">
                      {h.previewImage && (
                        <img src={h.previewImage} alt={style?.name} className="w-full h-full object-cover" />
                      )}
                    </div>
                    {isSel && (
                      <span className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#ff6b4a] text-white text-[11px] font-bold flex items-center justify-center shadow">
                        {order}
                      </span>
                    )}
                    <div className="px-2 py-1.5 bg-white">
                      <div className="text-[11px] font-semibold text-[#0f172a] truncate">{h.label || style?.name || "Custom"}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
